import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom'; // Used to get ID from URL
import Footer from '../../components/Footer';
import Navbar from '../../components/Header';

const BlogDetail = () => {
    const { id } = useParams(); // Get the blog post ID from the URL parameters
    const [blogPost, setBlogPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchBlogPost = async () => {
            try {
                setLoading(true); // Start loading
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/blog/${id}`);
                setBlogPost(res.data);
                window.scrollTo(0, 0); // Scroll to top on load
            } catch (err) {
                console.error("Error fetching blog post:", err);
                setError("Failed to load blog post. Please try again later.");
            } finally {
                setLoading(false); // End loading
            }
        };

        if (id) { // Only fetch if ID exists
            fetchBlogPost();
        }
    }, [id]); // Re-run effect if ID changes

    if (loading) {
        return (
            <>
                <Navbar diffHeader={true} />
                <div className="min-h-screen flex items-center justify-center bg-white">
                    <p className="text-gray-600 text-lg">Loading blog post...</p>
                </div>
                <Footer />
            </>
        );
    }

    if (error) {
        return (
            <>
                <Navbar diffHeader={true} />
                <div className="min-h-screen flex items-center justify-center bg-white">
                    <p className="text-red-500 text-lg">{error}</p>
                </div>
                <Footer />
            </>
        );
    }

    if (!blogPost) {
        return (
            <>
                <Navbar diffHeader={true} />
                <div className="min-h-screen flex items-center justify-center bg-white">
                    <p className="text-gray-600 text-lg">Blog post not found.</p>
                </div>
                <Footer />
            </>
        );
    }

    // Function to safely render HTML content
    const createMarkup = (htmlContent) => {
        return { __html: htmlContent };
    };

    return (
        <>
            <Navbar diffHeader={true} />
            <div className="min-h-screen bg-white font-inter text-gray-900">
                <div className="h-24 sm:h-32"></div> {/* Spacer for header */}

                <main className="mx-auto px-4 py-8 sm:px-6 lg:px-8 max-w-4xl">
                    <article className="bg-white rounded-lg shadow-lg overflow-hidden">
                        {blogPost.image && (
                            <div className="relative h-64 sm:h-96 w-full">
                                <img
                                    src={blogPost.image}
                                    alt={blogPost.title}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = `https://placehold.co/800x400/CCCCCC/333333?text=Image+Not+Found`;
                                    }}
                                />
                                {blogPost.category && (
                                    <span className="absolute bottom-4 left-4 bg-[#173d73] text-white text-sm font-semibold px-4 py-2 rounded-full">
                                        {blogPost.category}
                                    </span>
                                )}
                            </div>
                        )}

                        <div className="p-6 sm:p-8">
                            <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800 tracking-tight mb-4 leading-tight">
                                {blogPost.title}
                            </h1>

                            <p className="text-xl text-gray-700 mb-6 leading-relaxed">
                                {blogPost.description}
                            </p>

                            <div
                                className="prose prose-lg max-w-none text-gray-800 leading-relaxed text-left"
                                dangerouslySetInnerHTML={createMarkup(blogPost.content || blogPost.fullText)} // Use 'content' or 'fullText' based on your API
                            />
                        </div>
                    </article>

                    {/* Back button or Related posts section can go here */}
                    <div className="mt-10 text-center">
                        <button
                            onClick={() => window.history.back()}
                            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-[#173d73] hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Back to Blogs
                        </button>
                    </div>

                </main>
            </div>
            <Footer />
        </>
    );
};

export default BlogDetail;